import { useAdMeshContext } from '../context/AdMeshContext';

/**
 * Hook to access AdMesh SDK and tracking state
 *
 * Must be used within an <AdMeshProvider>
 *
 * @returns Object with SDK instance and tracking methods
 *
 * @example
 * ```tsx
 * const { sdk, sessionId, markMessageAsProcessed } = useAdMesh();
 *
 * // Use SDK to show recommendations
 * await sdk?.showRecommendations({
 *   query: 'user query',
 *   containerId: 'recommendations-container',
 *   sessionId,
 *   messageId
 * });
 *
 * // Mark message as processed to avoid duplicates
 * markMessageAsProcessed(messageId);
 * ```
 */
export function useAdMesh() {
  const context = useAdMeshContext();

  return {
    /** AdMesh SDK instance */
    sdk: context.sdk,

    /** API key */
    apiKey: context.apiKey,

    /** Session ID */
    sessionId: context.sessionId,

    /** Theme configuration */
    theme: context.theme,

    /** User language in BCP 47 format (e.g., "en-US") - from AdMeshProvider */
    language: context.language,

    /** Detailed user location from AdMeshProvider */
    location: context.location,

    /** Anonymous hashed user ID - from AdMeshProvider */
    userId: context.userId,

    /** AI model identifier (e.g., "gpt-4o") - from AdMeshProvider */
    model: context.model,

    /** Conversation history - from AdMeshProvider */
    messages: context.messages,

    /** Set of processed message IDs (for deduplication) */
    processedMessageIds: context.processedMessageIds,

    /** Ephemeral per-session recommendation results keyed by messageId */
    recommendationResults: context.recommendationResults,
    activeDelegationSession: context.activeDelegationSession,

    /** Mark a message as processed to prevent duplicate recommendations */
    markMessageAsProcessed: context.markMessageAsProcessed,

    /** Check if a message has already been processed */
    isMessageProcessed: context.isMessageProcessed,

    /** Store the recommendation result for a message within the current provider session */
    setRecommendationResult: context.setRecommendationResult,

    /** Read a stored recommendation result for a message within the current provider session */
    getRecommendationResult: context.getRecommendationResult,

    /** Check whether a recommendation result exists for a message within the current provider session */
    hasRecommendationResult: context.hasRecommendationResult,
    setActiveDelegationSession: context.setActiveDelegationSession,
    getActiveDelegationSession: context.getActiveDelegationSession,
    hasActiveDelegationSession: context.hasActiveDelegationSession,
    refreshDelegationActivity: context.refreshDelegationActivity,
    stopDelegationSession: context.stopDelegationSession,
  };
}

export default useAdMesh;
